import { SurveyTypes } from '#shared/types/Survey'

export type InteractionType =
    | 'likert-scale' // Traditional 1-5 scale
    | 'slider' // Visual slider for continuous values
    | 'card-selection' // Select cards that resonate
    | 'drag-ranking' // Drag to rank items
    | 'scenario-choice' // Choose between scenarios
    | 'free-text-ai' // Free text with AI analysis
    | 'visual-selection' // Select from images/icons
    | 'swipe-cards' // Tinder-like swipe interface
    | 'word-association' // Quick word associations
    | 'matrix-selection' // Grid selection for multiple dimensions
    | 'emoji-scale' // Use emojis for emotional responses
    | 'story-completion' // Complete stories/scenarios

export interface TestFeatures {
    hasAIAnalysis?: boolean // AI analyzes responses
    hasInstantFeedback?: boolean // Show feedback after each answer
    hasProgressGamification?: boolean // Badges, points, achievements
    hasTimeChallenge?: boolean // Time-based questions
    hasAdaptiveQuestions?: boolean // Questions adapt based on previous answers
    hasVisualElements?: boolean // Heavy use of images/graphics
    hasSocialComparison?: boolean // Compare with others
    hasPersonalizedPath?: boolean // Different paths based on answers
}

export interface SebapoznanieTest {
    slug: string
    title: string
    subtitle: string
    icon: string
    description: string
    duration: string
    questions: number
    surveyType: SurveyTypes
    iconBgClass: string
    borderClass: string
    componentName: string
    interactionTypes?: InteractionType[] // Multiple interaction types per test
    features?: TestFeatures
    funFactor?: 'relaxing' | 'engaging' | 'challenging' | 'playful' | 'thoughtful'
    hidden?: boolean // Temporarily hide from display
    meta: {
        title: string
        description: string
    }
}

export const useSebapoznanieTests = () => {
    const tests: SebapoznanieTest[] = [
        {
            slug: 'osobnostny-dotaznik',
            title: 'Osobnostný dotazník',
            subtitle: 'Kompletný profil',
            icon: '🧠',
            description: 'Kompletný osobnostný profil vrátane tímovej práce a hodnôt',
            duration: '15 min',
            questions: 92,
            surveyType: SurveyTypes.SELF_KNOWLEDGE,
            iconBgClass: 'bg-purple-100',
            borderClass: 'border-purple-200 hover:border-purple-400',
            componentName: 'SurveyForm',
            interactionTypes: ['likert-scale', 'matrix-selection', 'drag-ranking'],
            features: {
                hasProgressGamification: true,
                hasInstantFeedback: true,
                hasPersonalizedPath: true
            },
            funFactor: 'engaging',
            meta: {
                title: 'Osobnostný dotazník | Sebapoznanie | GROWNi',
                description: 'Kompletný osobnostný profil - spoznaj sa do hĺbky.'
            }
        },
        {
            slug: 'big-five',
            title: 'Big Five osobnosť',
            subtitle: 'Big 5',
            icon: '🎭',
            description: 'Otvorenosť, svedomitosť, extraverzia, prívetivosť a stabilita',
            duration: '3 min',
            questions: 30,
            surveyType: SurveyTypes.BIG_FIVE,
            iconBgClass: 'bg-blue-100',
            borderClass: 'border-blue-200 hover:border-blue-400',
            componentName: 'BigFiveSurveyForm',
            interactionTypes: ['slider', 'emoji-scale'],
            features: {
                hasVisualElements: true,
                hasInstantFeedback: true,
                hasSocialComparison: true
            },
            funFactor: 'playful',
            meta: {
                title: 'Big Five osobnosť | GROWNi',
                description: 'Dotazník Big Five - zisti svoj osobnostný profil'
            }
        },
        {
            slug: 'moralny-vyvoj',
            title: 'Morálny vývoj',
            subtitle: 'Kohlberg',
            icon: '⚖️',
            description: 'Zisti úroveň svojho morálneho uvažovania a etických hodnôt',
            duration: '15 min',
            questions: 20,
            surveyType: SurveyTypes.KOHLBERG,
            iconBgClass: 'bg-orange-100',
            borderClass: 'border-orange-200 hover:border-orange-400',
            componentName: 'KohlbergSurveyForm',
            interactionTypes: ['scenario-choice', 'story-completion', 'free-text-ai'],
            features: {
                hasAIAnalysis: true,
                hasAdaptiveQuestions: true
            },
            funFactor: 'thoughtful',
            meta: {
                title: 'Morálny vývoj | Sebapoznanie | GROWNi',
                description: 'Kohlberg test morálneho vývoja - pochop svoje etické uvažovanie.'
            }
        },
        {
            slug: 'hodnoty',
            title: 'Hodnoty',
            subtitle: 'Schwartz',
            icon: '💎',
            description: 'Objav svoje základné hodnoty a životné priority',
            duration: '6 min',
            questions: 20,
            surveyType: SurveyTypes.SCHWARTZ,
            iconBgClass: 'bg-indigo-100',
            borderClass: 'border-indigo-200 hover:border-indigo-400',
            componentName: 'SchwartzSurveyForm',
            interactionTypes: ['card-selection', 'drag-ranking'],
            features: {
                hasVisualElements: true,
                hasProgressGamification: true
            },
            funFactor: 'engaging',
            meta: {
                title: 'Hodnoty | Sebapoznanie | GROWNi',
                description: 'Schwartz test hodnôt - zisti, čo je pre teba v živote najdôležitejšie.'
            }
        },
        {
            slug: 'karierne-zaujmy',
            title: 'Kariérne záujmy',
            subtitle: 'Holland',
            icon: '💼',
            description: 'Identifikuj svoje profesijné záujmy a ideálne kariérne cesty',
            duration: '15 min',
            questions: 72,
            surveyType: SurveyTypes.HOLLAND,
            iconBgClass: 'bg-green-100',
            borderClass: 'border-green-200 hover:border-green-400',
            componentName: 'HollandSurveyForm',
            interactionTypes: ['visual-selection', 'swipe-cards'],
            features: {
                hasVisualElements: true,
                hasPersonalizedPath: true,
                hasInstantFeedback: true
            },
            funFactor: 'playful',
            meta: {
                title: 'Kariérne záujmy | Sebapoznanie | GROWNi',
                description: 'Holland test kariérnych záujmov - nájdi svoju ideálnu kariéru.'
            }
        },
        {
            slug: 'motivacia',
            title: 'Motivácia',
            subtitle: 'SDT',
            icon: '🔥',
            description: 'Preskúmaj svoje dôvody pri práci, učení a pomoci druhým',
            duration: '3 min',
            questions: 15,
            surveyType: SurveyTypes.SDT,
            iconBgClass: 'bg-red-100',
            borderClass: 'border-red-200 hover:border-red-400',
            componentName: 'SDTSurveyForm',
            meta: {
                title: 'Motivácia | Sebapoznanie | GROWNi',
                description: 'Reflexia inšpirovaná SDT - preskúmaj svoje dôvody v rôznych oblastiach.'
            }
        },
        {
            slug: 'rastove-myslenie',
            title: 'Rastové myslenie',
            subtitle: 'Dweck',
            icon: '📈',
            description: 'Zisti svoj prístup k učeniu a osobnému rastu',
            duration: '5 min',
            questions: 15,
            surveyType: SurveyTypes.GROWTH_MINDSET,
            iconBgClass: 'bg-teal-100',
            borderClass: 'border-teal-200 hover:border-teal-400',
            componentName: 'GrowthMindsetSurveyForm',
            meta: {
                title: 'Rastové myslenie | Sebapoznanie | GROWNi',
                description: 'Test rastového myslenia - zisti, ako pristupuješ k výzvam a rastu.'
            }
        },
        {
            slug: 'charakterove-silne-stranky',
            title: 'Charakterové silné stránky',
            subtitle: 'Cnosti',
            icon: '💪',
            description: 'Objav svoje top 5 charakterových silných stránok',
            duration: '10 min',
            questions: 96,
            surveyType: SurveyTypes.VIA,
            iconBgClass: 'bg-yellow-100',
            borderClass: 'border-yellow-200 hover:border-yellow-400',
            componentName: 'VIASurveyForm',
            meta: {
                title: 'Charakterové silné stránky a cnosti | Sebapoznanie | GROWNi',
                description: 'Test charakterových silných stránok a cností - objav svoje prirodzené talenty.'
            }
        },
        {
            slug: 'dospely-vyvoj',
            title: 'Dospelý vývoj',
            subtitle: 'Kegan',
            icon: '🌱',
            description: 'Pochop svoju vývojovú úroveň v dospelosti',
            duration: '12 min',
            questions: 20,
            surveyType: SurveyTypes.KEGAN,
            iconBgClass: 'bg-cyan-100',
            borderClass: 'border-cyan-200 hover:border-cyan-400',
            componentName: 'KeganSurveyForm',
            meta: {
                title: 'Dospelý vývoj | Sebapoznanie | GROWNi',
                description: 'Kegan test dospelého vývoja - zisti svoju vývojovú úroveň.'
            }
        },
        {
            slug: 'vztahove-styly',
            title: 'Vzťahové štýly',
            subtitle: 'Teória väzby',
            icon: '❤️',
            description: 'Preskúmaj svoj štýl vytvárania vzťahov',
            duration: '6 min',
            questions: 16,
            surveyType: SurveyTypes.ATTACHMENT,
            iconBgClass: 'bg-pink-100',
            borderClass: 'border-pink-200 hover:border-pink-400',
            componentName: 'AttachmentSurveyForm',
            meta: {
                title: 'Vzťahové štýly | Sebapoznanie | GROWNi',
                description: 'Test vzťahovej väzby - pochop svoje vzťahové vzorce.'
            }
        },
        {
            slug: 'podnikavost',
            title: 'Podnikavosť',
            subtitle: 'Podnikanie',
            icon: '🚀',
            description: 'Zmeraj svoj podnikateľský potenciál',
            duration: '3 min',
            questions: 35,
            surveyType: SurveyTypes.ENTREPRENEURSHIP,
            iconBgClass: 'bg-amber-100',
            borderClass: 'border-amber-200 hover:border-amber-400',
            componentName: 'EntrepreneurshipSurveyForm',
            meta: {
                title: 'Podnikavosť | Sebapoznanie | GROWNi',
                description: 'Test podnikateľského potenciálu - zisti svoju podnikavosť.'
            }
        },
        {
            slug: 'index-excelencie',
            title: 'Index excelencie',
            subtitle: 'Excelencia',
            icon: '🏆',
            description: 'Hodnotenie tvojho potenciálu pre výnimočnosť',
            duration: '3 min',
            questions: 30,
            surveyType: SurveyTypes.EXCELLENCE,
            iconBgClass: 'bg-yellow-100',
            borderClass: 'border-gold-200 hover:border-gold-400',
            componentName: 'ExcellenceSurveyForm',
            meta: {
                title: 'Index excelencie | Sebapoznanie | GROWNi',
                description: 'Index excelencie - meraj svoj potenciál pre výnimočnosť.'
            }
        },
        {
            slug: 'chut-pomoct-slovensku',
            title: 'Chuť pomôcť Slovensku',
            subtitle: 'Občianstvo',
            icon: '🇸🇰',
            description: 'Ako môžeš prispieť k lepšej spoločnosti',
            duration: '8 min',
            questions: 20,
            surveyType: SurveyTypes.HELP_SLOVAKIA,
            iconBgClass: 'bg-sky-100',
            borderClass: 'border-sky-200 hover:border-sky-400',
            componentName: 'HelpSlovakiaSurveyForm',
            meta: {
                title: 'Chuť pomôcť Slovensku | Sebapoznanie | GROWNi',
                description: 'Test občianskej angažovanosti - ako môžeš prispieť k lepšiemu Slovensku.'
            }
        },
        {
            slug: 'styly-vedenia',
            title: 'Štýly vedenia',
            subtitle: 'Goleman',
            icon: '👔',
            description: 'Zisti svoj dominantný štýl vedenia zo 6 Golemanových štýlov',
            duration: '10 min',
            questions: 20,
            surveyType: SurveyTypes.LEADERSHIP_STYLES,
            iconBgClass: 'bg-purple-100',
            borderClass: 'border-purple-200 hover:border-purple-400',
            componentName: 'LeadershipStylesSurveyForm',
            interactionTypes: ['card-selection'],
            features: {
                hasInstantFeedback: true
            },
            funFactor: 'engaging',
            meta: {
                title: 'Štýly vedenia | Sebapoznanie | GROWNi',
                description: 'Test štýlov vedenia podľa Daniela Golemana - zisti, ako vedieš tím.'
            }
        },
        {
            slug: 'organizacne-paradigmy',
            title: 'Organizačná kultúra',
            subtitle: 'Laloux',
            icon: '🏢',
            description: 'Zisti, v akom type organizačnej kultúry sa nachádzaš',
            duration: '8 min',
            questions: 20,
            surveyType: SurveyTypes.LALOUX_PARADIGMS,
            iconBgClass: 'bg-teal-100',
            borderClass: 'border-teal-200 hover:border-teal-400',
            componentName: 'LalouxParadigmsSurveyForm',
            interactionTypes: ['card-selection'],
            features: {
                hasInstantFeedback: true
            },
            funFactor: 'thoughtful',
            meta: {
                title: 'Organizačná kultúra | Sebapoznanie | GROWNi',
                description: 'Test organizačných paradigiem podľa Laloux - pochop svoju organizačnú kultúru.'
            }
        },
        {
            slug: 'urovne-vedomia',
            title: 'Úrovne vedomia',
            subtitle: 'Kegan',
            icon: '🧬',
            description: 'Zisti svoju úroveň kognitívnej komplexity podľa Roberta Kegana',
            duration: '10 min',
            questions: 20,
            surveyType: SurveyTypes.KEGAN_ORDERS,
            iconBgClass: 'bg-indigo-100',
            borderClass: 'border-indigo-200 hover:border-indigo-400',
            componentName: 'KeganOrdersSurveyForm',
            interactionTypes: ['card-selection'],
            features: {
                hasInstantFeedback: true
            },
            funFactor: 'thoughtful',
            meta: {
                title: 'Úrovne vedomia | Sebapoznanie | GROWNi',
                description: 'Test úrovní vedomia podľa Kegana - pochop svoju vývojovú úroveň.'
            }
        },
        {
            slug: 'urovne-pocuvania',
            title: 'Úrovne počúvania',
            subtitle: 'CTI',
            icon: '👂',
            description: 'Zisti, na akej úrovni počúvaš najčastejšie',
            duration: '6 min',
            questions: 20,
            surveyType: SurveyTypes.LISTENING_LEVELS,
            iconBgClass: 'bg-sky-100',
            borderClass: 'border-sky-200 hover:border-sky-400',
            componentName: 'ListeningLevelsSurveyForm',
            interactionTypes: ['card-selection'],
            features: {
                hasInstantFeedback: true
            },
            funFactor: 'relaxing',
            meta: {
                title: 'Úrovne počúvania | Sebapoznanie | GROWNi',
                description: 'Test úrovní počúvania podľa CTI - zisti svoj štýl komunikácie.'
            }
        }
    ]

    // Filter out hidden tests for display
    const visibleTests = tests.filter((test) => !test.hidden)

    const getTestBySlug = (slug: string) => {
        return tests.find((test) => test.slug === slug)
    }

    const getTestBySurveyType = (surveyType: SurveyTypes) => {
        return tests.find((test) => test.surveyType === surveyType)
    }

    return {
        tests: visibleTests, // Only return visible tests
        allTests: tests, // Include hidden tests (for admin/internal use)
        getTestBySlug,
        getTestBySurveyType
    }
}
